import { createTheme } from '@mui/material/styles';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#4F46E5', // Indigo 600 - modern, premium
            light: '#EEF2FF',
            dark: '#3730A3',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#7C3AED', // Violet 600
            light: '#F5F3FF',
            dark: '#5B21B6',
            contrastText: '#FFFFFF',
        },
        success: {
            main: '#059669', // Emerald 600
            light: '#ECFDF5',
            dark: '#047857',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#D97706', // Amber 600
            light: '#FFFBEB',
            dark: '#B45309',
            contrastText: '#FFFFFF',
        },
        error: {
            main: '#DC2626', // Red 600
            light: '#FEF2F2',
            dark: '#B91C1C',
            contrastText: '#FFFFFF',
        },
        info: {
            main: '#0284C7', // Sky 600
            light: '#F0F9FF',
            dark: '#0369A1',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#F8FAFC', // Slate 50
            paper: '#FFFFFF',
        },
        text: {
            primary: '#0F172A', // Slate 900
            secondary: '#64748B', // Slate 500
            disabled: '#94A3B8',
        },
        divider: '#E2E8F0', // Slate 200
    },
    typography: {
        fontFamily: [
            '"Plus Jakarta Sans"',
            '"Inter"',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            'sans-serif',
        ].join(','),
        h1: { fontWeight: 800, letterSpacing: '-0.025em' },
        h2: { fontWeight: 700, letterSpacing: '-0.025em' },
        h3: { fontWeight: 700, letterSpacing: '-0.02em' },
        h4: { fontWeight: 700, letterSpacing: '-0.02em', color: '#0F172A' },
        h5: { fontWeight: 600, letterSpacing: '-0.01em', color: '#0F172A' },
        h6: { fontWeight: 600, letterSpacing: '-0.01em', color: '#0F172A' },
        subtitle1: { fontWeight: 500, color: '#475569' },
        subtitle2: { fontWeight: 500, color: '#64748B' },
        body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
        body2: { fontSize: '0.875rem', lineHeight: 1.57 },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    },
    shape: {
        borderRadius: 12,
    },
    shadows: [
        'none',
        '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        '0 1px 3px 0 rgba(0, 0, 0, 0.07), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
        '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)',
        '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
        '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
        ...Array(18).fill('0 10px 20px rgba(0, 0, 0, 0.05)')
    ],
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: '#F8FAFC',
                    color: '#0F172A',
                    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
                },
                '::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '::-webkit-scrollbar-thumb': {
                    backgroundColor: '#CBD5E1',
                    borderRadius: '4px',
                },
                '::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#94A3B8',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.88)',
                    backdropFilter: 'blur(16px)',
                    color: '#0F172A',
                    boxShadow: 'none',
                    borderBottom: '1px solid #E2E8F0',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    borderRadius: 14,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.03)',
                },
                elevation1: {
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.03)',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '8px 18px',
                    boxShadow: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.18)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)',
                    },
                },
                containedSecondary: {
                    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 48,
                    '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                        backgroundColor: '#4F46E5',
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    letterSpacing: '0.02em',
                    minHeight: 48,
                    color: '#64748B',
                    '&.Mui-selected': {
                        color: '#4F46E5',
                        fontWeight: 700,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #F1F5F9',
                    padding: '14px 16px',
                    fontSize: '0.875rem',
                },
                head: {
                    backgroundColor: '#F8FAFC',
                    fontWeight: 700,
                    color: '#475569',
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: '#F8FAFC',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 8,
                    fontSize: '0.75rem',
                    height: 26,
                },
                sizeSmall: {
                    height: 22,
                    fontSize: '0.7rem',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 18,
                    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                    border: '1px solid #E2E8F0',
                },
            },
        },
    },
});

export default theme;